<template>
  <div class="flex flex-col">
    <NForm ref="formRef" :model="formValues">
      <NGrid :cols="1" :yGap="4">
        <NFormItemGridItem :label="t('canvasUI.canvasBuilder.embeddable.form.application')">
          <CSelect
            v-model:value="formValues.appName"
            bordered
            :onUpdate:value="setAppDefaults"
            :options="appListOptions"
            :placeholder="t('canvasUI.canvasBuilder.embeddable.form.selectApplication')"
          />
        </NFormItemGridItem>

        <NFormItemGridItem>
          <template #label>
            <div class="flex items-center gap-2">
              <span>{{ t('canvasUI.canvasBuilder.embeddable.form.width') }}</span>
              <CHelpText v-if="isScribbleChild">
                {{ t('canvasUI.canvasBuilder.embeddable.form.dimensionsLockedHelp') }}
              </CHelpText>
            </div>
          </template>
          <NInput
            v-model:value.trim="formValues.width"
            clearable
            placeholder=""
            :readonly="isScribbleChild"
            :show-button="false"
          />
        </NFormItemGridItem>

        <NFormItemGridItem>
          <template #label>
            <div class="flex items-center gap-2">
              <span>{{ t('canvasUI.canvasBuilder.embeddable.form.height') }}</span>
              <CHelpText v-if="isScribbleChild">
                {{ t('canvasUI.canvasBuilder.embeddable.form.dimensionsLockedHelp') }}
              </CHelpText>
            </div>
          </template>
          <NInput
            v-model:value.trim="formValues.height"
            clearable
            placeholder=""
            :readonly="isScribbleChild"
            :show-button="false"
          />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="isFullScreenModeEnabled && !isScribbleChild"
          :label="t('canvasUI.canvasBuilder.embeddable.form.fullScreenMode')"
        >
          <NCheckbox v-model:checked="formValues.fullScreenMode">
            {{ t('canvasUI.canvasBuilder.embeddable.form.fullScreenModeDescription') }}
          </NCheckbox>
        </NFormItemGridItem>
      </NGrid>
    </NForm>
    <div v-if="selectedAppSettingsConfiguration" class="mb-15">
      <CConfigEditor
        ref="settingsEditorRef"
        :initial-values="initialSettings"
        :loading="false"
        :template="selectedAppSettingsConfiguration"
      />
    </div>
    <div
      class="absolute bottom-0 left-0 z-2 mx-8 py-4"
      :style="{
        width: 'calc(100% - 2 * var(--n-body-padding))',
        backgroundColor: themeVars.base,
      }"
    >
      <CButton block class="mt-auto" :disabled="!formValues.appName" type="primary" @click="onSave">
        {{ t('canvasUI.common.done') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, toRef, inject, type ComputedRef } from 'vue'
import { NForm, NFormItemGridItem, NGrid, NInput, NCheckbox, type FormInst } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CSelect from '@lib/components/CSelect/CSelect.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CHelpText from '@lib/components/CHelpText/CHelpText.vue'
import type { EmbedLocation } from '@lib/types/app'
import useCanvas from '@canvas-builder/composables/useCanvas'
import type { EmbeddableProps } from '@canvas-builder/types/canvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import { useFetchApps } from '@lib/api/apps/apps.queries'
import { loadCanvasAndDsrApps } from '@lib/composables/useInstalledApps'
import CConfigEditor from '@lib/components/CConfigEditor/CConfigEditor.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { getCleanUrl } from '@sdk/utils/cleanUrl'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = defineProps<{
  id: string
  data: EmbeddableProps
}>()

const themeVars = useThemeVars()
const { t } = useI18n()

// Inject LaunchDarkly flags
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { updateNodeDataById, setComponentEditMode, getParentType } = useCanvas()
const { data: appList, isFetching: isFetchingApps } = useFetchApps(loadCanvasAndDsrApps)
const formRef = ref<FormInst | null>(null)
const settingsEditorRef = ref<null | InstanceType<typeof CConfigEditor>>(null)
const appListOptions = computed(
  () =>
    appList.value?.map((a) => ({
      label: a.app_metadata?.display_name ?? a.app_metadata?.name ?? '',
      value: a.app_metadata?.name ?? '',
    })) ?? [],
)
const formValues = reactive({
  type: props.data?.type,
  appName: props.data?.app_name,
  width: props.data.width,
  height: props.data.height,
  fullScreenMode: props.data.full_screen_mode ?? false,
})

const parentType = getParentType(toRef(props.id))
const isScribbleChild = computed(() => {
  return parentType.value === ComponentTypes.Scribble
})

const isFullScreenModeEnabled = computed(() => launchDarkly.value?.enable_embeddable_full_screen_mode ?? false)

const selectedAppMetadata = computed(
  () => appList.value?.find((a) => a.app_metadata?.name === formValues.appName)?.app_metadata ?? null,
)
const selectedAppSettingsConfiguration = computed(() => {
  return selectedAppMetadata.value?.module?.canvas?.settings ?? selectedAppMetadata.value?.module?.dsr?.settings ?? null
})

const initialSettings = computed(() => ({
  // make sure we pick up the most recent default values when we edit a component
  ...(selectedAppMetadata.value?.module?.canvas?.defaults?.settings || {}),
  ...(props.data.settings || {}),
}))
async function onSave() {
  let embeddableInstanceSettings: Record<string, any> | null = null
  if (selectedAppSettingsConfiguration.value && settingsEditorRef.value) {
    const { success, config } = await settingsEditorRef.value.validate()

    if (!success) return
    else embeddableInstanceSettings = config
  }

  formRef.value?.validate((errors) => {
    if (!errors) {
      const app = appList.value?.find((a) => a.app_metadata?.name === formValues.appName)
      if (!app) throw new Error(`[Embeddable.settings]: App "${formValues.appName}" not found`)

      const availableLocations = ['canvas', 'dsr'] satisfies EmbedLocation[]

      updateNodeDataById(props.id, {
        app_name: formValues.appName,
        width: formValues.width,
        height: formValues.height,
        full_screen_mode: formValues.fullScreenMode,
        src: getCleanUrl(app?.content_url ?? '') ?? '',
        type: formValues.type,
        embed_locations: availableLocations.filter((l) => app.app_metadata?.module?.[l]?.enabled),
        headless_locations: availableLocations.filter((l) => app.app_metadata?.module?.[l]?.headless),
        ...(embeddableInstanceSettings ? { settings: embeddableInstanceSettings } : {}),
        properties_by_embed_location: {
          canvas: { start_in_loading_mode: app.app_metadata?.module?.canvas?.start_in_loading_mode ?? false },
          dsr: { start_in_loading_mode: app.app_metadata?.module?.dsr?.start_in_loading_mode ?? false },
        },
        file: {
          id: app.id,
          name: app.name,
          content_url: app.content_url,
          content_type: app.content_type,
          content_extension: app.content_extension,
          thumbnail_url: app.thumbnail_url,
          content_length: app.content_length,
          content_thumbnails: null,
        },
      } satisfies EmbeddableProps)
      setComponentEditMode(false)
    }
  })
}

function setAppDefaults(appName: string) {
  const app = appList.value?.find((a) => a.app_metadata?.name === appName)
  if (!app) throw new Error(`[Embeddable.settings]: App "${appName}" not found when setting defaults`)

  const { width = '', height = '' } =
    app.app_metadata?.module?.canvas?.defaults?.dimensions ?? app.app_metadata?.module?.dsr?.defaults?.dimensions ?? {}
  if (width) formValues.width = width
  if (height) formValues.height = height
}

defineExpose({
  isBusy: isFetchingApps,
})
</script>
